import { useState } from "react";
import { api, ApiError } from "../net/api.js";
import { authErrorTranslationKey } from "../net/auth-errors.js";
import { refreshSession } from "../net/session.js";
import { Button, ErrorNote, Panel } from "../ui/primitives.js";
import { TurnstileWidget, useTurnstilePolicy } from "../ui/Turnstile.js";
import { useLocale, type Locale } from "../i18n.js";
import "./login.css";

type Mode = "login" | "register-email" | "register-code" | "reset-email" | "reset-code";

export function Login({ wide = false }: { wide?: boolean }) {
  const { locale, setLocale, t } = useLocale();
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [devCode, setDevCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [guestBusy, setGuestBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const captchaRequired = useTurnstilePolicy();
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaResetKey, setCaptchaResetKey] = useState(0);

  const codeMode = mode === "register-code" || mode === "reset-code";
  const isRegister = mode === "register-email" || mode === "register-code";
  const isReset = mode === "reset-email" || mode === "reset-code";
  const captchaMode = mode === "login" || mode === "register-email" || mode === "reset-email";

  function clearError() { setError(null); }
  function go(next: Mode) { setMode(next); setCode(""); setPassword(""); setConfirm(""); setDevCode(null); setCaptchaToken(null); setCaptchaResetKey((key) => key + 1); clearError(); }

  /**
   * Start playing without an account.
   *
   * Deliberately the first thing on the card in sign-in mode: the account is
   * what someone gets *after* deciding they like the game, not what they have
   * to produce before seeing it.
   */
  async function playAsGuest() {
    clearError(); setGuestBusy(true);
    try {
      await api.guest();
      await refreshSession();
    } catch (caught) {
      setError(caught instanceof ApiError
        ? t(authErrorTranslationKey(caught.code, caught.status, false))
        : t("authServiceUnavailable"));
    } finally { setGuestBusy(false); }
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault(); clearError();
    const needsCaptcha = captchaMode && captchaRequired !== false;
    if (needsCaptcha && captchaToken === null) { setError(captchaRequired === null ? t("captchaUnavailable") : t("captchaRequired")); return; }
    setBusy(true);
    const token = needsCaptcha ? captchaToken ?? undefined : undefined;
    try {
      if (mode === "login") { await api.login(email.trim(), password, token); await refreshSession(); return; }
      if (mode === "register-email" || mode === "reset-email") {
        const result = mode === "register-email" ? await api.registerCode(email.trim(), locale, token) : await api.resetCode(email.trim(), locale, token);
        setDevCode(result.devCode ?? null); setMode(mode === "register-email" ? "register-code" : "reset-code"); return;
      }
      if (password !== confirm) { setError(t("passwordMismatch")); return; }
      if (isRegister) await api.register(email.trim(), code.trim(), password, locale);
      else await api.resetPassword(email.trim(), code.trim(), password);
      await refreshSession();
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(t(authErrorTranslationKey(caught.code, caught.status, codeMode)));
      } else setError(t("authServiceUnavailable"));
    } finally { setBusy(false); setCaptchaToken(null); setCaptchaResetKey((key) => key + 1); }
  }

  const title = mode === "login" ? t("signIn") : isRegister ? t("createAccount") : t("resetPassword");
  const submitLabel = mode === "login" ? t("signIn") : codeMode ? (isRegister ? t("createAccount") : t("resetPassword")) : t("sendCode");

  return (
    <div className={`app-frame login${wide ? " app-frame-wide login-wide" : ""}`}>
      <div className="login-hero">
        <div className="language-switch" role="group" aria-label={t("language")}>
          {(["en", "zh-CN"] as Locale[]).map((option) => (
            <button key={option} type="button" className={locale === option ? "language-on" : ""} onClick={() => setLocale(option)}>
              {option === "en" ? t("english") : t("chinese")}
            </button>
          ))}
        </div>
        <div className="login-orbit" aria-hidden="true"><span>♠</span><span>♥</span><span>♦</span></div>
        <h1 className="login-title">{t("brand")}</h1>
        <p className="login-sub">{t("tagline")}</p>
      </div>
      <Panel className="login-card">
        <div className="auth-heading"><h2>{title}</h2><p>{isReset ? t("resetHint") : isRegister ? t("registerHint") : t("loginHint")}</p></div>
        {mode === "login" && (
          <div className="guest-entry">
            <Button type="button" variant="primary" size="lg" busy={guestBusy} className="login-submit" onClick={() => void playAsGuest()}>{t("guestPlay")}</Button>
            <p className="section-hint">{t("guestPlayHint")}</p>
            <div className="guest-entry-rule" aria-hidden="true" />
          </div>
        )}
        <form onSubmit={submit}>
          {(mode === "login" || mode === "register-email" || mode === "reset-email") && (
            <label className="field"><span className="field-label">{t("email")}</span><input className="field-input" type="email" inputMode="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("emailPlaceholder")} /></label>
          )}
          {mode === "login" && <label className="field"><span className="field-label">{t("password")}</span><input className="field-input" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} /></label>}
          {codeMode && <>
            <label className="field"><span className="field-label">{t("codeSent", { email })}</span><input className="field-input field-code tabular" type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required autoFocus value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" /></label>
            <label className="field"><span className="field-label">{t("newPassword")}</span><input className="field-input" type="password" autoComplete="new-password" required minLength={8} maxLength={72} value={password} onChange={(e) => setPassword(e.target.value)} /></label>
            <label className="field"><span className="field-label">{t("confirmPassword")}</span><input className="field-input" type="password" autoComplete="new-password" required minLength={8} maxLength={72} value={confirm} onChange={(e) => setConfirm(e.target.value)} /></label>
          </>}
          {captchaMode && captchaRequired !== false && <TurnstileWidget enabled={captchaRequired === true} resetKey={captchaResetKey} onToken={setCaptchaToken} />}
          {devCode !== null && <button type="button" className="devcode" onClick={() => setCode(devCode)}>{t("devCode", { code: devCode })}</button>}
          <Button type="submit" variant="primary" size="lg" busy={busy} className="login-submit">{submitLabel}</Button>
          {error !== null && <ErrorNote>{error}</ErrorNote>}
        </form>
        <div className="auth-links">
          {mode === "login" && <><button type="button" onClick={() => go("register-email")}>{t("createAccount")}</button><button type="button" onClick={() => go("reset-email")}>{t("forgotPassword")}</button></>}
          {codeMode && <button type="button" onClick={() => go(isRegister ? "register-email" : "reset-email")}>{t("sendAgain")}</button>}
          {mode !== "login" && <button type="button" onClick={() => go("login")}>{t("backToSignIn")}</button>}
        </div>
      </Panel>
    </div>
  );
}
